import { CATALOG_FIELD_IDS } from "@/fwk.generated/field-catalog/catalogFieldIds";
import type { ImeSeriousnessPreview } from "@/lib/api/generated/casedb";
import { CODING_ACTION, parseCoding, type CodingState } from "@/features/meddra/shared/model/meddraCoding";
import type { CaseCriteriaValue, CaseFieldValue, CaseFieldValues } from "../../shared/model/caseReview";
import { IME_SERIOUSNESS_TEXT } from "../constants/imeSeriousnessLabels";

/** The key of a field in its event record: the last part of its catalog field id. */
function recordKey(fieldId: string): string {
  return fieldId.slice(fieldId.lastIndexOf(".") + 1);
}

/** The review drafts' key of the events collection: the record keys above hang under it. */
export const IME_EVENTS_COLLECTION = CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_PREFERRED_TERM
  .slice(0, CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_PREFERRED_TERM.lastIndexOf("."));

/** The fields of the event that the EMA IME list reads and fills, by their key in the event record. */
export const IME_SERIOUSNESS_KEYS = {
  switch: recordKey(CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_SERIOUSNESS_FROM_IME),
  preferredTerm: recordKey(CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_PREFERRED_TERM),
  coding: recordKey(CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_MEDDRA_CODING),
  criteria: recordKey(CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_SERIOUSNESS_CRITERIA),
  notes: recordKey(CATALOG_FIELD_IDS.CASE_EVENT_EVENTS_SERIOUSNESS_CRITERIA_OTHER),
} as const;

/**
 * What the list judges for one event: its preferred term (PT) as the save will store it. A dictionary coding that
 * names only its lower level term (a Theragenie proposal) is asked about by that term; the server gives its PT.
 */
export type ImeTarget =
  | { kind: "term"; preferredTerm: string }
  | { kind: "llt"; lltCode: string; version: string; language: string }
  | { kind: "none" };

const NO_TERM: ImeTarget = { kind: "none" };

/** A PT, or no PT when the text is blank. */
function termOf(value: unknown): ImeTarget {
  return typeof value === "string" && value.trim() !== "" ? { kind: "term", preferredTerm: value.trim() } : NO_TERM;
}

/** The unsaved MedDRA coding command of the event, if any. A saved coding state carries no action. */
function pendingCoding(value: CaseFieldValue | undefined): CodingState | undefined {
  try {
    const state = parseCoding(value);
    return state.action ? state : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The event's PT as the save will store it. An unsaved MedDRA coding decides it as the server projects it: a selected
 * dictionary term gives that term's PT, "Enter a value" keeps only the entered values (an event's has no PT), and
 * "Clear coding" leaves no PT. Otherwise the PT field decides, as typed or as saved.
 */
export function imeTargetOf(values: CaseFieldValues): ImeTarget {
  const coding = pendingCoding(values[IME_SERIOUSNESS_KEYS.coding]);
  if (coding?.action === CODING_ACTION.select) {
    if (coding.term?.pt?.trim()) return termOf(coding.term.pt);
    if (coding.lltCode && coding.version) {
      return { kind: "llt", lltCode: coding.lltCode, version: coding.version, language: coding.language ?? "en" };
    }
    return NO_TERM;
  }
  if (coding?.action === CODING_ACTION.manual || coding?.action === CODING_ACTION.clear) {
    return termOf(coding.raw?.[IME_SERIOUSNESS_KEYS.preferredTerm]);
  }
  return termOf(values[IME_SERIOUSNESS_KEYS.preferredTerm]);
}

/** One key per target, so that a change of the PT is a change of the key. Case and spaces do not count. */
export function imeTargetKey(target: ImeTarget): string {
  if (target.kind === "term") {
    return `term:${target.preferredTerm.replace(/[\u200B-\u200D\u2060\uFEFF]/g, "").replace(/\s+/g, " ").trim().toLowerCase()}`;
  }
  if (target.kind === "llt") return `llt:${target.version}|${target.language}|${target.lltCode}`;
  return "none";
}

/** The PT to name on screen: the list's own spelling when it has one. */
export function imeTermLabel(preview: ImeSeriousnessPreview): string {
  return preview.list_preferred_term ?? preview.preferred_term ?? "";
}

/** The lines of the confirmation: the product owner's text, what it does to this event, and when it is stored. */
export function confirmationLines(preview: ImeSeriousnessPreview): string[] {
  const outcome = !preview.applies ? IME_SERIOUSNESS_TEXT.noPreferredTerm
    : preview.on_list ? IME_SERIOUSNESS_TEXT.onList(imeTermLabel(preview))
      : IME_SERIOUSNESS_TEXT.notOnList(imeTermLabel(preview));
  return [IME_SERIOUSNESS_TEXT.body(preview.list_version ?? ""), outcome,
    IME_SERIOUSNESS_TEXT.laterChange, IME_SERIOUSNESS_TEXT.saveNote];
}

/** The confirmation, ready to draw: still loading, failed, or the texts and the values that an accept applies. */
export type ImeConfirmation =
  | { status: "loading"; message: string }
  | { status: "failed"; message: string }
  | { status: "ready"; lines: string[]; preview: ImeSeriousnessPreview };

/** One change that applying the list makes to an event field: a new value, or back to the saved value. */
export type ImeFieldChange =
  | { key: string; action: "set"; value: CaseFieldValue }
  | { key: string; action: "restore" };

/** The criteria that are ticked; a criterion that is false or not set is not ticked. */
function ticked(value: CaseFieldValue | undefined): string {
  if (!value || typeof value !== "object") return "";
  return Object.entries(value as CaseCriteriaValue).filter(([, flag]) => flag === true).map(([key]) => key).sort().join(",");
}

/** Notes compared as a reader sees them: no text and blank text are both empty. */
function notesText(value: CaseFieldValue | undefined): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The changes that make the event's criteria and notes what the list gives, and nothing when they already are. A value
 * that is the saved one again goes back to the saved state, decision included. With no PT the list gives nothing.
 */
export function imeFieldChanges(preview: ImeSeriousnessPreview, values: CaseFieldValues, saved: CaseFieldValues): ImeFieldChange[] {
  if (!preview.applies) return [];
  const criteria: CaseCriteriaValue = preview.seriousness_criteria ?? {};
  const notes = preview.seriousness_notes ?? null;
  const changes: ImeFieldChange[] = [];
  const criteriaKey = IME_SERIOUSNESS_KEYS.criteria;
  if (ticked(values[criteriaKey]) !== ticked(criteria)) {
    changes.push(ticked(saved[criteriaKey]) === ticked(criteria)
      ? { key: criteriaKey, action: "restore" } : { key: criteriaKey, action: "set", value: criteria });
  }
  const notesKey = IME_SERIOUSNESS_KEYS.notes;
  if (notesText(values[notesKey]) !== notesText(notes)) {
    changes.push(notesText(saved[notesKey]) === notesText(notes)
      ? { key: notesKey, action: "restore" } : { key: notesKey, action: "set", value: notes });
  }
  return changes;
}
